import React from 'react';
import { useIsMobile } from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { ModalV2 } from '@dapp/multi-lib/ui/V2Modal';
import { getNetworkFromChainId } from '@dapp/multi-lib/network';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { Input } from './../components/form/Input';
import { FormControl } from './../components/form/FormControl';
import Paragraph from 'structure/launchpad/components/Typography';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { ethers } from 'ethers';
import { InputTokenAmount } from 'structure/staking/components/form/InputTokenAmount';
import { InputLockedPeriod } from 'structure/staking/components/form/InputLockedPeriod';
import { StakeDetails } from 'structure/staking/components/StakeDetails';
import {
  FIRST_DEFAULT_LOCK_DAYS,
  FIRST_MIN_LOCK_DAYS,
  useDeposit,
} from 'structure/staking/hooks/useStakingContract';
import { formatUnits, parseUnits } from 'utils/units';
import { waitForTransaction } from 'utils/waitForTransaction';
import {
  useStakingDepositCalcInfo,
  useStakingPoolData,
  useStakingPoolList,
  useStakingUserPoolInfo,
  useUpdatePoolMetadata,
  useWalletSREP,
} from 'structure/staking/hooks/useStakingAPI';
import { daysToSeconds, secondsToDate, secondsToDays } from 'utils/date';
import { ActionSteps } from 'structure/staking/components/ActionSteps';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createStakeValidation } from 'structure/staking/utils/createValidationSchema';
import { useConnection } from 'wagmi';
import { useParams } from 'next/navigation';
import { Typography } from '@dapp/multi-lib/sdaoui/Typography';
import { formatDateDayMonYear } from 'utils/formateDate';
import { useAllowance, useApprove } from 'utils/hooks/useERC20Contract';
import { Title2 } from 'components/Typography';
import { getTxErrorMessage } from 'utils/getTxErrorMessage';
import { getStaticAssetUrl } from 'utils/url';
import { StakingPool } from 'api/StakingAPI';
import { useTokenBalance } from 'utils/hooks/useTokenBalance';

type FormData = {
  tokenAmount?: string;
  lockedPeriod?: number;
};

type DepositModalProps = {
  open: boolean;
  poolId: number;
  onOpenChange: (isOpen: boolean) => void;
};

export function ModalStake({ open, poolId, onOpenChange }: DepositModalProps) {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile(834);
  const toastV2 = useToast();
  const account = useConnection();
  const params = useParams<{ id: string }>();

  const { data = [] } = useStakingPoolList({}, params?.id ?? '');
  const poolData = data.find((pool) => pool?.id === poolId);
  const stakingData = useStakingPoolData(poolData as StakingPool);
  const pool = stakingData.data;
  const networkInfo = getNetworkFromChainId(pool?.chainId);

  const { data: userBalance } = useTokenBalance({
    address: account.address,
    token: pool?.depositTokenAddress,
    chainId: pool?.chainId,
    query: {
      enabled: !!account.address && open,
    },
  });

  const schema = createStakeValidation({
    tokenAmount: {
      label: t('STAKING.TOKEN_AMOUNT'),
      min: ethers.utils.formatUnits(1, pool?.depositTokenDecimals),
      max: formatUnits(userBalance?.value || 0, pool?.depositTokenDecimals),
      required: true,
      decimals: pool?.depositTokenDecimals,
    },
    lockedPeriod: {
      label: t('STAKING.LOCKED_PERIOD'),
      min: FIRST_MIN_LOCK_DAYS,
      max: secondsToDays(Number(pool?.MAX_LOCKING_PERIOD)),
      required: true,
    },
  });

  const form = useForm<FormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: { tokenAmount: '', lockedPeriod: FIRST_DEFAULT_LOCK_DAYS },
  });

  const lockedPeriod = form.watch('lockedPeriod');
  const tokenAmount = form.watch('tokenAmount');

  const bnTokenAmount = parseUnits(
    tokenAmount || '0',
    pool?.depositTokenDecimals
  );

  const handleClickMax = () => {
    form.setValue(
      'tokenAmount',
      formatUnits(userBalance?.value || 0, pool?.depositTokenDecimals)
    );

    form.trigger('tokenAmount');
  };

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const handleChangeLockedPeriod = (value: number) => {
    form.setValue('lockedPeriod', value);
    form.trigger('lockedPeriod');
  };

  const approve = useApprove({
    tokenAmount: bnTokenAmount.toString(),
    tokenAddress: pool?.depositTokenAddress,
    spenderAddress: pool?.stakingContractAddress,
    chainId: pool?.chainId,
    query: {
      enabled: form.formState.isValid && open,
    },
  });

  const allowance = useAllowance({
    tokenAddress: pool?.depositTokenAddress,
    addressOwner: account.address as `0x${string}`,
    addressSpender: pool?.stakingContractAddress,
    chainId: pool?.chainId,
    query: {
      enabled: form.formState.isValid && account.address && open,
    },
  });

  const bnAllowanceAmount = ethers.BigNumber.from(allowance.data || 0);
  const requiredAllowance = bnTokenAmount.gt(bnAllowanceAmount);

  const deposit = useDeposit({
    contractAddress: pool?.stakingContractAddress,
    tokenAmount: bnTokenAmount.toString(),
    lockedPeriod: daysToSeconds(lockedPeriod),
    enabled:
      bnTokenAmount.gt(0) &&
      form.formState.isValid &&
      !requiredAllowance &&
      open,
  });

  const walletSREPData = useWalletSREP({
    walletAddress: account.address as `0x${string}`,
    query: {
      enabled: !!account.address && open,
    },
  });

  const userPoolInfo = useStakingUserPoolInfo({
    poolContractAddress: pool?.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool?.chainId,
    pool,
  });

  const updatePoolData = useUpdatePoolMetadata();

  const handleDeposit = async () => {
    if (deposit.prepare.isError) {
      toastV2({
        title: deposit.prepare.error.message,
        description: t('STAKING.FAILED_TO_REQUEST_DEPOSIT'),
        type: 'error',
      });
      return;
    }

    try {
      const result = await deposit.contract.writeContractAsync(
        deposit.prepare.data!.request
      );

      await waitForTransaction({
        chainId: pool?.chainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      await updatePoolData.mutateAsync();

      toastV2({
        title: t('STAKING.DEPOSIT_SUCCESS'),
        description: `Code: ${result}`,
        type: 'success',
      });

      await walletSREPData.refetch();
      handleOpenChange(false);
    } catch (error) {
      console.log('### deposit error', error);
      const message = getTxErrorMessage(
        error as Error,
        t('common:ERROR_MESSAGES.UNKNOWN_ERROR')
      );

      toastV2({
        title: t('ERROR_MESSAGES.ERROR'),
        description: message,
        type: 'error',
      });
    }
  };

  const stakingCalculation = useStakingDepositCalcInfo({
    poolContractAddress: pool?.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool?.chainId,
    lockedPeriod: daysToSeconds(lockedPeriod),
    tokenAmount: bnTokenAmount.toString(),
    pool: pool,
    query: {
      enabled: !!account.address && bnTokenAmount.gt(0) && open,
    },
  });

  const handleApprove = async () => {
    if (approve.prepare.isError) {
      toastV2({
        title: approve.prepare.error.message,
        description: t('STAKING.FAILED_TO_REQUEST_APPROVE'),
        type: 'error',
      });
      return;
    }

    try {
      const result = await approve.contract.writeContractAsync(
        approve.prepare.data!.request
      );

      await waitForTransaction({
        chainId: pool?.chainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      toastV2({
        title: t('STAKING.APPROVE_SUCCESS'),
        description: `Code: ${result}`,
        type: 'success',
      });

      await allowance.refetch();
    } catch (error) {
      console.log('### approve error', error);
      const message = getTxErrorMessage(
        error as Error,
        t('common:ERROR_MESSAGES.UNKNOWN_ERROR')
      );

      toastV2({
        title: t('ERROR_MESSAGES.ERROR'),
        description: message,
        type: 'error',
      });
    }
  };

  const onSubmit = async () => {
    if (requiredAllowance) {
      return await handleApprove();
    }

    await handleDeposit();
  };

  return (
    <ModalV2.Root modal={true} open={open} onOpenChange={handleOpenChange}>
      <ModalV2.Content
        variant='dark'
        fullscreen={isMobile}
        scrollable
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='flex flex-row items-center justify-between'>
            <ModalV2.Title variant='dark'>
              <Title2>{t('STAKING.MODAL_STAKE_TITLE')}</Title2>
            </ModalV2.Title>
            <ModalV2.Close variant='dark' />
          </div>
          <ModalV2.Body variant='dark' className='rounded-t-main mb-5 pt-3'>
            <FormControl
              label={
                <div className='flex flex-row'>
                  <Paragraph className='text-sm text-[#B4BCD0]'>
                    {t('STAKING.NETWORK')}
                  </Paragraph>
                </div>
              }
            >
              <Input
                value=''
                type='text'
                disabled
                startEnhancer={
                  <div className='flex items-center gap-2'>
                    <Image
                      alt={networkInfo.nativeCurrency.symbol}
                      src={getStaticAssetUrl(
                        `/sfi/static/image/token/${networkInfo.nativeCurrency.symbol}.svg`
                      )}
                      height={32}
                      width={32}
                    />
                    <Paragraph className='text-sm font-normal'>
                      {networkInfo.name ?? networkInfo.shortName}
                    </Paragraph>
                  </div>
                }
              />
            </FormControl>

            <InputTokenAmount
              tokenSymbol={pool?.depositTokenSymbol}
              error={form.formState.errors.tokenAmount?.message}
              onMax={handleClickMax}
              max={formatUnits(
                userBalance?.value || 0,
                pool?.depositTokenDecimals
              )}
              label={
                <div className='flex flex-row justify-end'>
                  <Paragraph className='text-xs	text-[#B4BCD0]'>
                    {t('STAKING.BALANCE')} :{' '}
                    {t('tokenAmount', {
                      value: formatUnits(
                        userBalance?.value || 0,
                        pool?.depositTokenDecimals
                      ),
                    })}{' '}
                    {pool?.depositTokenSymbol}
                  </Paragraph>
                </div>
              }
              {...form.register('tokenAmount')}
            />

            <div className='flex flex-row justify-between'>
              <Typography as='p' className='text-sm'>
                {t('STAKING.CHOOSE_LOCKED_PERIOD')}
              </Typography>
              <Typography as='p' className='text-sm'>
                {formatDateDayMonYear(
                  secondsToDate(daysToSeconds(lockedPeriod) + Date.now() / 1000)
                )}
              </Typography>
            </div>

            <InputLockedPeriod
              value={form.getValues().lockedPeriod}
              onChanePeriod={handleChangeLockedPeriod}
              error={form.formState.errors.lockedPeriod?.message}
              lockingPeriods={pool?.defaultLockingPeriods}
              {...form.register('lockedPeriod')}
            />
          </ModalV2.Body>
          <ModalV2.Body variant='dark' className='rounded-t-main pt-3'>
            <StakeDetails
              userPoolInfo={userPoolInfo.data}
              pool={pool}
              action='stake'
              loading={stakingCalculation.isFetching}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              calculation={stakingCalculation.data as any}
            />
            <ActionSteps
              approve={requiredAllowance}
              deposit={!requiredAllowance}
            />
          </ModalV2.Body>
          <div className='mt-6 flex justify-end gap-4'>
            <Button
              onClick={() => handleOpenChange(false)}
              disabled={
                approve.contract.isPending || deposit.contract.isPending
              }
              style='outline'
            >
              {t('STAKING.CANCEL')}
            </Button>
            {!requiredAllowance ? (
              <Button
                type='submit'
                loading={
                  deposit.contract.isPending ||
                  deposit.prepare.isFetching ||
                  form.formState.isSubmitting
                }
                disabled={
                  deposit.contract.isPending ||
                  deposit.prepare.isFetching ||
                  form.formState.isSubmitting ||
                  !form.formState.isValid
                }
              >
                {t('STAKING.DEPOSIT')}
              </Button>
            ) : (
              <Button
                type='submit'
                loading={
                  approve.contract.isPending ||
                  approve.prepare.isFetching ||
                  form.formState.isSubmitting
                }
                disabled={
                  approve.contract.isPending ||
                  approve.prepare.isFetching ||
                  form.formState.isSubmitting ||
                  !form.formState.isValid
                }
              >
                {t('STAKING.APPROVE')}
              </Button>
            )}
          </div>
        </form>
      </ModalV2.Content>
    </ModalV2.Root>
  );
}
